(() => {
  if (window.C2BShippingEstimatorLoaded === true) {
    return;
  }

  window.C2BShippingEstimatorLoaded = true;

  const ROOT_SELECTOR = '[data-c2b-shipping-estimator]';
  const READY_ATTR = 'c2bShippingEstimatorReady';

  const PROFILE_LABELS = {
    general_profile: 'curier',
    house_nordic: 'House Nordic',
    kooduu: 'Kooduu',
    flonal: 'Flonal',
    pemode_furniture: 'Pemode Furniture',
  };

  const CURRENCY = 'lei';

  const FLONAL_RANGES = [
    { min: 0, max: 127500, price: 30000, label: '0-1.275,00 lei' },
    { min: 127501, max: 178500, price: 40800, label: '1.275,01-1.785,00 lei' },
    { min: 178501, max: 229400, price: 51000, label: '1.785,01-2.294,00 lei' },
    { min: 229401, max: 280300, price: 63700, label: '2.294,01-2.803,00 lei' },
    { min: 280301, max: 331300, price: 76400, label: '2.803,01-3.313,00 lei' },
  ];
  const DENMARK_FURNITURE_RANGES = [
    { min: 0, max: 30000, price: 21300, label: '0-30 kg' },
    { min: 30001, max: 50000, price: 130000, label: '30,01-50 kg' },
    { min: 50001, max: 75000, price: 170000, label: '50,01-75 kg' },
    { min: 75001, max: 100000, price: 185000, label: '75,01-100 kg' },
    { min: 100001, max: 150000, price: 190000, label: '100,01-150 kg' },
    { min: 150001, max: 200000, price: 220000, label: '150,01-200 kg' },
    { min: 200001, max: Infinity, price: 315000, label: 'peste 200 kg' },
  ];

  // Pemode Furniture uses the same commercial formula as the general courier
  // above 30 kg: 120 lei + 4 lei for every whole kg above 30 kg.
  // Shopify is configured with integer-kilogram bands (e.g. 125-125.99 kg),
  // so the estimator intentionally uses Math.floor(), not Math.ceil().
  const PEMODE_FURNITURE_WEIGHT_WINDOWS = [
    { min: 125, max: 140 },
    { min: 250, max: 280 },
    { min: 375, max: 400 },
  ];

  const PEMODE_BASE_WEIGHT_KG = 30;
  const PEMODE_BASE_PRICE_CENTS = 12000;
  const PEMODE_PRICE_PER_EXTRA_KG_CENTS = 400;

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const moneyFormatter = new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const compactNumberFormatter = new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const formatMoney = (cents) => moneyFormatter.format((Number(cents) || 0) / 100);

  const formatWeight = (weightKg) => {
    const value = Number(weightKg);

    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }

    return `${compactNumberFormatter.format(value)} kg`;
  };

  const formatQuantity = (quantity) => `${quantity} buc.`;

  const getQuantity = (root) => {
    const formId = root.dataset.formId;
    const sectionId = root.dataset.sectionId;
    const escapedFormId = formId ? cssEscape(formId) : '';

    const input =
      (escapedFormId && document.querySelector(`input[name="quantity"][form="${escapedFormId}"]`)) ||
      (sectionId && document.getElementById(`Quantity-${sectionId}`)) ||
      root.closest('product-info')?.querySelector('quantity-input input[name="quantity"]') ||
      document.querySelector('quantity-input input[name="quantity"]');

    const value = Number.parseInt(input?.value, 10);

    if (!Number.isFinite(value) || value < 1) {
      return 1;
    }

    return value;
  };

  const getCurrentVariantId = (root) => {
    const formId = root.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    const input =
      form?.querySelector('input.product-variant-id[name="id"]') ||
      root.closest('product-info')?.querySelector('input.product-variant-id[name="id"]') ||
      document.querySelector('input.product-variant-id[name="id"]');

    const value = Number.parseInt(input?.value, 10);

    return Number.isFinite(value) ? value : null;
  };

  const parseJsonFromScript = (root, selector, fallback) => {
    const script = root.querySelector(selector);

    if (!script?.textContent) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(script.textContent);
      return parsed == null ? fallback : parsed;
    } catch (error) {
      console.warn('[C2B shipping estimator] Could not parse JSON data.', error);
      return fallback;
    }
  };

  const parseVariants = (root) => {
    const parsed = parseJsonFromScript(root, '[data-c2b-shipping-estimator-variants]', []);
    return Array.isArray(parsed) ? parsed : [];
  };

  const parseCartItems = (root) => {
    const parsed = parseJsonFromScript(root, '[data-c2b-shipping-estimator-cart-items]', []);
    return Array.isArray(parsed) ? parsed : [];
  };

  const getRange = (value, ranges) => ranges.find((item) => value >= item.min && value <= item.max) || null;

  const getDenmarkFurnitureRange = (weightGrams) => getRange(weightGrams, DENMARK_FURNITURE_RANGES);

  const getPemodeFurnitureRange = (weightGrams) => {
    const chargedKg = Math.floor((Number(weightGrams) || 0) / 1000);
    const isCovered = PEMODE_FURNITURE_WEIGHT_WINDOWS.some(
      ({ min, max }) => chargedKg >= min && chargedKg <= max
    );

    if (!isCovered) {
      return null;
    }

    return {
      price:
        PEMODE_BASE_PRICE_CENTS +
        (chargedKg - PEMODE_BASE_WEIGHT_KG) * PEMODE_PRICE_PER_EXTRA_KG_CENTS,
      label: `${compactNumberFormatter.format(chargedKg)}-${compactNumberFormatter.format(chargedKg)},99 kg`,
    };
  };

  const GENERAL_STANDARD_RANGES = [
    { max: 1, price: 2290, label: '0-1 kg' },
    { max: 2, price: 2590, label: '1,01-2 kg' },
    { max: 3, price: 2790, label: '2,01-3 kg' },
    { max: 4, price: 2990, label: '3,01-4 kg' },
    { max: 5, price: 3190, label: '4,01-5 kg' },
    { max: 6, price: 3390, label: '5,01-6 kg' },
    { max: 7, price: 3590, label: '6,01-7 kg' },
    { max: 8, price: 3890, label: '7,01-8 kg' },
    { max: 9, price: 4090, label: '8,01-9 kg' },
    { max: 10, price: 4290, label: '9,01-10 kg' },
    { max: 11, price: 4490, label: '10,01-11 kg' },
    { max: 12, price: 4690, label: '11,01-12 kg' },
    { max: 13, price: 4890, label: '12,01-13 kg' },
    { max: 14, price: 5190, label: '13,01-14 kg' },
    { max: 15, price: 5390, label: '14,01-15 kg' },
    { max: 16, price: 5590, label: '15,01-16 kg' },
    { max: 17, price: 5790, label: '16,01-17 kg' },
    { max: 18, price: 5990, label: '17,01-18 kg' },
    { max: 19, price: 6190, label: '18,01-19 kg' },
    { max: 20, price: 6490, label: '19,01-20 kg' },
    { max: 21, price: 6690, label: '20,01-21 kg' },
    { max: 22, price: 6890, label: '21,01-22 kg' },
    { max: 23, price: 7090, label: '22,01-23 kg' },
    { max: 24, price: 7290, label: '23,01-24 kg' },
    { max: 25, price: 7490, label: '24,01-25 kg' },
    { max: 26, price: 7690, label: '25,01-26 kg' },
    { max: 27, price: 7990, label: '26,01-27 kg' },
    { max: 28, price: 8190, label: '27,01-28 kg' },
    { max: 29, price: 8390, label: '28,01-29 kg' },
    { max: 30, price: 8590, label: '29,01-30 kg' },
    { max: 31, price: 9190, label: '30,01-31 kg' },
    { max: 32, price: 9890, label: '31,01-32 kg' },
    { max: 33, price: 10490, label: '32,01-33 kg' },
    { max: 34, price: 11190, label: '33,01-34 kg' },
    { max: 35, price: 11790, label: '34,01-35 kg' },
    { max: 36, price: 12390, label: '35,01-36 kg' },
    { max: 37, price: 13090, label: '36,01-37 kg' },
    { max: 38, price: 13690, label: '37,01-38 kg' },
    { max: 39, price: 14290, label: '38,01-39 kg' },
    { max: 40, price: 14990, label: '39,01-40 kg' },
    { max: 41, price: 15590, label: '40,01-41 kg' },
    { max: 42, price: 16190, label: '41,01-42 kg' },
    { max: 43, price: 16890, label: '42,01-43 kg' },
    { max: 44, price: 17490, label: '43,01-44 kg' },
    { max: 45, price: 18090, label: '44,01-45 kg' },
    { max: 46, price: 18790, label: '45,01-46 kg' },
    { max: 47, price: 19390, label: '46,01-47 kg' },
    { max: 48, price: 19990, label: '47,01-48 kg' },
    { max: 49, price: 20690, label: '48,01-49 kg' },
    { max: 50, price: 21290, label: '49,01-50 kg' },
    { max: 51, price: 21990, label: '50,01-51 kg' },
    { max: 52, price: 22590, label: '51,01-52 kg' },
    { max: 53, price: 23190, label: '52,01-53 kg' },
    { max: 54, price: 23890, label: '53,01-54 kg' },
    { max: 55, price: 24490, label: '54,01-55 kg' },
    { max: 56, price: 25090, label: '55,01-56 kg' },
    { max: 57, price: 25790, label: '56,01-57 kg' },
    { max: 58, price: 26390, label: '57,01-58 kg' },
    { max: 59, price: 26990, label: '58,01-59 kg' },
    { max: 60, price: 27690, label: '59,01-60 kg' },
  ];

  const getGeneralRange = (weightKg) => {
    const value = Number(weightKg);

    if (!Number.isFinite(value) || value < 0 || value > 60) {
      return null;
    }

    return GENERAL_STANDARD_RANGES.find((range) => value <= range.max) || null;
  };

  const buildWeightDetails = () => [];

  const estimateShipping = ({ profileKey, quantity, totalWeightGrams, totalWeightKg, totalCents }) => {
    if (profileKey === 'house_nordic') {
      const denmarkFurnitureRange = getDenmarkFurnitureRange(totalWeightGrams);

      if (!denmarkFurnitureRange) {
        return {
          status: 'unknown',
          label: PROFILE_LABELS.house_nordic,
          value: 'Nu poate fi estimat',
          details: buildWeightDetails({
            quantity,
            totalWeightKg,
          }),
          note: 'Nu există o regulă de transport acoperită pentru această configurație. Costul final se confirmă în checkout.',
        };
      }

      return {
        status: 'available',
        label: PROFILE_LABELS.house_nordic,
        cents: denmarkFurnitureRange.price,
        rangeLabel: denmarkFurnitureRange.label,
        details: buildWeightDetails({
          quantity,
          totalWeightKg,
        }),
        note: 'Costul final se confirmă în checkout.',
      };
    }

    if (profileKey === 'kooduu') {
      return {
        status: 'available',
        label: PROFILE_LABELS.kooduu,
        cents: 10000,
        details: [],
        note: 'Costul final se confirmă în checkout.',
      };
    }

    if (profileKey === 'flonal') {
      const flonalRange = getRange(totalCents, FLONAL_RANGES);

      if (!flonalRange) {
        return {
          status: 'unknown',
          label: PROFILE_LABELS.flonal,
          value: 'Nu poate fi estimat',
          details: [],
          note: 'Pentru această valoare, transportul nu poate fi estimat automat. Costul final se confirmă în checkout.',
        };
      }

      return {
        status: 'available',
        label: PROFILE_LABELS.flonal,
        cents: flonalRange.price,
        rangeLabel: flonalRange.label,
        details: [],
        note: 'Costul final se confirmă în checkout.',
      };
    }

    if (profileKey === 'pemode_furniture') {
      const pemodeRange = getPemodeFurnitureRange(totalWeightGrams);

      if (!pemodeRange) {
        return {
          status: 'unknown',
          label: PROFILE_LABELS.pemode_furniture,
          value: 'Nu poate fi estimat',
          details: buildWeightDetails({
            quantity,
            totalWeightKg,
          }),
          note: 'Greutatea totală nu este acoperită de regulile Pemode Furniture. Costul final se confirmă în checkout.',
        };
      }

      return {
        status: 'available',
        label: PROFILE_LABELS.pemode_furniture,
        cents: pemodeRange.price,
        rangeLabel: pemodeRange.label,
        details: buildWeightDetails({
          quantity,
          totalWeightKg,
        }),
        note: 'Costul final se confirmă în checkout.',
      };
    }

    const generalRange = getGeneralRange(totalWeightKg);

    if (!generalRange) {
      return {
        status: 'unknown',
        label: PROFILE_LABELS.general_profile,
        value: 'Nu poate fi estimat',
        details: buildWeightDetails({
          quantity,
          totalWeightKg,
        }),
        note: 'Nu există o regulă de transport acoperită pentru această configurație. Costul final se confirmă în checkout.',
      };
    }

    return {
      status: 'available',
      label: PROFILE_LABELS.general_profile,
      cents: generalRange.price,
      rangeLabel: generalRange.label,
      details: buildWeightDetails({
        quantity,
        totalWeightKg,
      }),
      note: 'Costul final se confirmă în checkout.',
    };
  };

  const getCartGroup = (groups, profileKey) => {
    if (!groups[profileKey]) {
      groups[profileKey] = {
        profileKey,
        quantity: 0,
        totalWeightGrams: 0,
        totalCents: 0,
      };
    }

    return groups[profileKey];
  };

  const buildCartGroups = (items) => {
    const groups = {};

    items.forEach((item) => {
      const quantity = Number.parseInt(item.quantity, 10);
      if (!Number.isFinite(quantity) || quantity < 1) return;

      const profileKey = item.profileKey || 'general_profile';
      const weightGrams = Number(item.weightGrams) || 0;
      const linePriceCents = Number(item.linePriceCents) || 0;
      const group = getCartGroup(groups, profileKey);

      group.quantity += quantity;
      group.totalWeightGrams += weightGrams * quantity;
      group.totalCents += linePriceCents;
    });

    return Object.values(groups);
  };

  const buildCartDetail = (group, result) => {
    const rawLabel = result.label || PROFILE_LABELS[group.profileKey] || PROFILE_LABELS.general_profile;
    const label = String(rawLabel).trim();
    const transportLabel = label.toLowerCase().startsWith('transport ') ? label : `Transport ${label}`;
    const price = result.cents != null ? formatMoney(result.cents) : result.value || 'Nu poate fi estimat';

    return `${transportLabel} - ${price}`;
  };

  const estimateCartShipping = (items) => {
    const groups = buildCartGroups(items);

    if (groups.length === 0) {
      return {
        status: 'unknown',
        value: 'Nu poate fi estimat',
        details: [],
        note: 'Coșul este gol sau nu conține produse cu transport calculabil.',
      };
    }

    let totalCents = 0;
    let hasUnknown = false;
    const details = [];

    groups.forEach((group) => {
      const result = estimateShipping({
        profileKey: group.profileKey,
        quantity: group.quantity,
        totalWeightGrams: group.totalWeightGrams,
        totalWeightKg: group.totalWeightGrams / 1000,
        totalCents: group.totalCents,
      });

      if (result.cents == null) {
        hasUnknown = true;
      } else {
        totalCents += result.cents;
      }

      details.push(buildCartDetail(group, result));
    });

    if (hasUnknown) {
      return {
        status: 'unknown',
        value: 'Nu poate fi estimat',
        details,
        note: 'Una sau mai multe reguli de transport nu sunt acoperite de estimator. Costul final se confirmă în checkout.',
      };
    }

    return {
      status: 'available',
      cents: totalCents,
      details,
      note: 'Costul final se confirmă în checkout.',
    };
  };

  const setState = (root, result) => {
    const valueNode = root.querySelector('[data-c2b-shipping-estimator-value]');
    const detailsNode = root.querySelector('[data-c2b-shipping-estimator-details]');
    const noteNode = root.querySelector('[data-c2b-shipping-estimator-note]');

    root.dataset.c2bShippingEstimatorStatus = result.status;

    if (valueNode) {
      valueNode.textContent = result.cents != null ? formatMoney(result.cents) : result.value || 'Nu poate fi estimat';
    }

    if (detailsNode) {
      const details = Array.isArray(result.details) ? result.details.filter(Boolean) : [];
      detailsNode.innerHTML = '';

      if (details.length > 0) {
        details.forEach((detail) => {
          const item = document.createElement('li');
          item.textContent = detail;
          detailsNode.appendChild(item);
        });

        detailsNode.hidden = false;
      } else {
        detailsNode.hidden = true;
      }
    }

    if (noteNode) {
      noteNode.textContent = result.note || 'Costul final se confirmă în checkout.';
    }
  };

  const updateProduct = (root) => {
    const variants = parseVariants(root);
    const variantId = getCurrentVariantId(root);
    const variant = variants.find((item) => Number(item.id) === variantId) || variants[0];

    if (!variant) {
      setState(root, {
        status: 'unknown',
        value: 'Nu poate fi estimat',
        note: 'Nu am putut identifica varianta selectată.',
      });
      return;
    }

    const quantity = getQuantity(root);
    const weightGrams = Number(variant.weightGrams) || 0;
    const priceCents = Number(variant.priceCents) || 0;
    const totalWeightGrams = weightGrams * quantity;
    const totalWeightKg = totalWeightGrams / 1000;
    const totalCents = priceCents * quantity;
    const profileKey = root.dataset.profileKey || 'general_profile';

    setState(root, estimateShipping({ profileKey, quantity, totalWeightGrams, totalWeightKg, totalCents }));
  };

  const updateCart = (root) => {
    const items = parseCartItems(root);
    setState(root, estimateCartShipping(items));
  };

  const update = (root) => {
    const mode = root.dataset.c2bShippingEstimatorMode || root.dataset.mode || 'product';

    if (mode === 'cart') {
      updateCart(root);
      return;
    }

    updateProduct(root);
  };

  const scheduleUpdate = (root) => {
    window.requestAnimationFrame(() => update(root));
  };

  const init = (root) => {
    if (!root || root.dataset[READY_ATTR] === 'true') {
      return;
    }

    root.dataset[READY_ATTR] = 'true';
    scheduleUpdate(root);
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  const scheduleAllUpdates = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(scheduleUpdate);
  };

  const observeDynamicRoots = () => {
    if (!window.MutationObserver || !document.documentElement) return;

    let queued = false;

    const observer = new MutationObserver((mutations) => {
      const hasEstimatorMutation = mutations.some((mutation) =>
        Array.from(mutation.addedNodes || []).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(ROOT_SELECTOR) || node.querySelector?.(ROOT_SELECTOR))
        )
      );

      if (!hasEstimatorMutation || queued) return;

      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        initAll();
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  document.addEventListener('input', (event) => {
    if (!event.target.matches('input[name="quantity"], input[name="updates[]"]')) return;

    scheduleAllUpdates();
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('input, select')) return;

    scheduleAllUpdates();
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('quantity-input .quantity__button, cart-remove-button')) return;

    window.setTimeout(scheduleAllUpdates, 0);
  });

  observeDynamicRoots();

  if (document.readyState !== 'loading') {
    initAll();
  }
})();
